import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@/config/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = API_BASE_URL;

const ROLE_CONFIG = [
  { key: "kitchen", label: "Kitchen Printer", optional: false },
  { key: "cashier", label: "Cashier Receipt Printer", optional: false },
  { key: "bar", label: "Bar Printer", optional: true },
  { key: "dessert", label: "Dessert Printer", optional: true },
];

const defaultRole = {
  enabled: false,
  printer_name: "",
  printer_type: "thermal",
  printer_connection: "usb",
  paper_size: "80",
  ip_address: "",
  usb_device: "",
  bluetooth_device: "",
};

const PrinterSettings = ({
  restaurantInfo,
  setRestaurantInfo,
  handleSave,
  selectedRestaurantId,
  canAdministerPrinters = false,
  canRetryPrintJobs = false,
}) => {
  const [diagnostics, setDiagnostics] = useState(restaurantInfo.printer_diagnostics || null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [printJobs, setPrintJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState(null);

  const printerRoles = useMemo(() => {
    const saved = restaurantInfo.printer_roles || {};
    return {
      kitchen: {
        ...defaultRole,
        enabled: true,
        printer_name: restaurantInfo.printer_name || "",
        printer_type: restaurantInfo.printer_type || "thermal",
        printer_connection: restaurantInfo.printer_connection || "usb",
        paper_size: restaurantInfo.paper_size || "80",
        ...(saved.kitchen || {}),
      },
      cashier: {
        ...defaultRole,
        enabled: true,
        printer_name: restaurantInfo.printer_name || "",
        printer_type: restaurantInfo.printer_type || "thermal",
        printer_connection: restaurantInfo.printer_connection || "usb",
        paper_size: restaurantInfo.paper_size || "80",
        ...(saved.cashier || {}),
      },
      bar: {
        ...defaultRole,
        ...(saved.bar || {}),
      },
      dessert: {
        ...defaultRole,
        ...(saved.dessert || {}),
      },
    };
  }, [restaurantInfo]);

  useEffect(() => {
    setDiagnostics(restaurantInfo.printer_diagnostics || null);
  }, [restaurantInfo.printer_diagnostics]);

  const updateBasePrinterField = (field, value) => {
    const nextRoles = {
      ...printerRoles,
      kitchen: {
        ...printerRoles.kitchen,
        [field]: value,
      },
    };
    setRestaurantInfo({
      ...restaurantInfo,
      [field]: value,
      printer_roles: nextRoles,
    });
  };

  const updateRoleField = (roleKey, field, value) => {
    const nextRoles = {
      ...printerRoles,
      [roleKey]: {
        ...printerRoles[roleKey],
        [field]: value,
      },
    };

    const nextRestaurantInfo = {
      ...restaurantInfo,
      printer_roles: nextRoles,
    };

    if (roleKey === "kitchen") {
      if (field === "printer_name") nextRestaurantInfo.printer_name = value;
      if (field === "printer_type") nextRestaurantInfo.printer_type = value;
      if (field === "printer_connection") nextRestaurantInfo.printer_connection = value;
      if (field === "paper_size") nextRestaurantInfo.paper_size = value;
    }

    setRestaurantInfo(nextRestaurantInfo);
  };

  const updateAutoPrint = (field, checked) => {
    const nextAutoPrint = {
      ...(restaurantInfo.printer_auto_print || {
        manual_orders: false,
        whatsapp_orders: false,
        online_orders: false,
      }),
      [field]: checked,
    };
    setRestaurantInfo({
      ...restaurantInfo,
      printer_auto_print: nextAutoPrint,
      auto_print: Boolean(nextAutoPrint.manual_orders || nextAutoPrint.whatsapp_orders || nextAutoPrint.online_orders),
    });
  };

  const updateCopiesByRole = (role, value) => {
    const nextCopiesByRole = {
      ...(restaurantInfo.printer_copies_by_role || { kitchen: 1, cashier: 1 }),
      [role]: value,
    };
    setRestaurantInfo({
      ...restaurantInfo,
      printer_copies_by_role: nextCopiesByRole,
      copies: nextCopiesByRole.kitchen || value,
    });
  };

  const fetchDiagnostics = useCallback(async () => {
    if (!selectedRestaurantId) return;
    setLoadingDiagnostics(true);
    try {
      const { data } = await axios.get(`${API}/restaurants/${selectedRestaurantId}/printer/diagnostics`, {
        withCredentials: true,
      });
      setDiagnostics(data);
      setRestaurantInfo((prev) => ({
        ...prev,
        printer_diagnostics: data,
      }));
    } catch (error) {
      toast.error("Unable to load printer diagnostics");
    } finally {
      setLoadingDiagnostics(false);
    }
  }, [selectedRestaurantId, setRestaurantInfo]);

  const fetchPrintJobs = useCallback(async () => {
    if (!selectedRestaurantId) return;
    setLoadingJobs(true);
    try {
      const { data } = await axios.get(
        `${API}/restaurants/${selectedRestaurantId}/print-jobs?limit=50`,
        { withCredentials: true }
      );
      setPrintJobs(data.filter((job) => !job.legacy && ["queued", "failed", "unknown"].includes(job.status)));
    } catch (error) {
      toast.error("Unable to load print queue");
    } finally {
      setLoadingJobs(false);
    }
  }, [selectedRestaurantId]);

  useEffect(() => {
    if (!selectedRestaurantId) return;
    fetchDiagnostics();
    fetchPrintJobs();
  }, [fetchDiagnostics, fetchPrintJobs, selectedRestaurantId]);

  const retryPrintJob = async (jobId) => {
    setRetryingJobId(jobId);
    try {
      await axios.post(`${API}/print-jobs/${jobId}/retry`,
        { reason: "operator_retry" },
        { withCredentials: true }
      );
      toast.success("Print job queued for retry");
      await fetchPrintJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to retry print job");
    } finally {
      setRetryingJobId(null);
    }
  };

  return (
    <Card className="border-border/40">
      <CardHeader>
        <CardTitle>Thermal Printer Settings</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">

        {!canAdministerPrinters && (
          <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-sm text-muted-foreground">
            Printer configuration requires a restaurant manager or administrator.
          </div>
        )}

        <div className="flex items-center gap-3">
          <Switch
            checked={restaurantInfo.printer_enabled}
            onCheckedChange={(v) => updateBasePrinterField("printer_enabled", v)}
          />
          <Label>Enable Printer</Label>
        </div>

        <div>
  <Label>Printer Type</Label>

  <Select
    value={restaurantInfo.printer_type}
    onValueChange={(value) => updateBasePrinterField("printer_type", value)}
  >
    <SelectTrigger>
      <SelectValue placeholder="Select Printer Type" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="thermal">
        Thermal (ESC/POS)
      </SelectItem>

      <SelectItem value="epson">
        Epson
      </SelectItem>

      <SelectItem value="star">
        Star Micronics
      </SelectItem>
    </SelectContent>
  </Select>
</div>

<div>
  <Label>Connection</Label>

  <Select
    value={restaurantInfo.printer_connection}
    onValueChange={(value) => updateBasePrinterField("printer_connection", value)}
  >
    <SelectTrigger>
      <SelectValue placeholder="Select Connection" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="usb">USB</SelectItem>
      <SelectItem value="network">Network (LAN)</SelectItem>
      <SelectItem value="bluetooth">Bluetooth</SelectItem>
    </SelectContent>
  </Select>
</div>

<div>
  <Label>Paper Size</Label>

  <Select
    value={restaurantInfo.paper_size}
    onValueChange={(value) => updateBasePrinterField("paper_size", value)}
  >
    <SelectTrigger>
      <SelectValue placeholder="Select Paper Size" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="80">80 mm</SelectItem>
      <SelectItem value="58">58 mm</SelectItem>
    </SelectContent>
  </Select>
</div>

        <div>
          <Label>Printer Name</Label>
          <Input
            value={restaurantInfo.printer_name}
            onChange={(e) => updateBasePrinterField("printer_name", e.target.value)}
          />
        </div>

<div>
  <Label>Number of Copies</Label>

  <Select
    value={String(restaurantInfo.copies || 1)}
    onValueChange={(value) => {
      const parsed = parseInt(value, 10);
      setRestaurantInfo({
        ...restaurantInfo,
        copies: parsed,
        printer_copies_by_role: {
          ...(restaurantInfo.printer_copies_by_role || { kitchen: 1, cashier: 1 }),
          kitchen: parsed,
          cashier: (restaurantInfo.printer_copies_by_role || {}).cashier || parsed,
        },
      });
    }}
  >
    <SelectTrigger>
      <SelectValue placeholder="Select Copies" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="1">1 Copy</SelectItem>
      <SelectItem value="2">2 Copies</SelectItem>
      <SelectItem value="3">3 Copies</SelectItem>
      <SelectItem value="4">4 Copies</SelectItem>
      <SelectItem value="5">5 Copies</SelectItem>
    </SelectContent>
  </Select>
</div>

        <div className="flex items-center gap-3">
          <Switch
            checked={restaurantInfo.auto_print}
            onCheckedChange={(v) =>
              setRestaurantInfo({
                ...restaurantInfo,
                auto_print: v,
                printer_auto_print: {
                  ...(restaurantInfo.printer_auto_print || {
                    manual_orders: false,
                    whatsapp_orders: false,
                    online_orders: false,
                  }),
                  manual_orders: v,
                },
              })
            }
          />
          <Label>Print Automatically</Label>
        </div>

        <div className="border-t border-border/40 pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Printer Roles</h3>
            <Badge variant="outline">Configuration</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ROLE_CONFIG.map((roleMeta) => {
              const role = printerRoles[roleMeta.key];
              return (
                <div key={roleMeta.key} className="rounded-md border border-border/50 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>{roleMeta.label}</Label>
                    <div className="flex items-center gap-2">
                      {roleMeta.optional && <span className="text-xs text-muted-foreground">Optional</span>}
                      <Switch
                        checked={Boolean(role.enabled)}
                        onCheckedChange={(v) => updateRoleField(roleMeta.key, "enabled", v)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Printer Name</Label>
                    <Input
                      value={role.printer_name || ""}
                      onChange={(e) => updateRoleField(roleMeta.key, "printer_name", e.target.value)}
                      placeholder={`${roleMeta.label} name`}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={role.printer_type || "thermal"}
                        onValueChange={(value) => updateRoleField(roleMeta.key, "printer_type", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="thermal">Thermal (ESC/POS)</SelectItem>
                          <SelectItem value="epson">Epson</SelectItem>
                          <SelectItem value="star">Star Micronics</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Connection</Label>
                      <Select
                        value={role.printer_connection || "usb"}
                        onValueChange={(value) => updateRoleField(roleMeta.key, "printer_connection", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usb">USB</SelectItem>
                          <SelectItem value="network">Network (LAN)</SelectItem>
                          <SelectItem value="bluetooth">Bluetooth (Future-ready)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {role.printer_connection === "network" && (
                    <div>
                      <Label className="text-xs">LAN IP Address</Label>
                      <Input
                        value={role.ip_address || ""}
                        onChange={(e) => updateRoleField(roleMeta.key, "ip_address", e.target.value)}
                        placeholder="192.168.1.50"
                      />
                    </div>
                  )}

                  {role.printer_connection === "usb" && (
                    <div>
                      <Label className="text-xs">USB Device (Optional)</Label>
                      <Input
                        value={role.usb_device || ""}
                        onChange={(e) => updateRoleField(roleMeta.key, "usb_device", e.target.value)}
                        placeholder="USB001"
                      />
                    </div>
                  )}

                  {role.printer_connection === "bluetooth" && (
                    <div>
                      <Label className="text-xs">Bluetooth Device (Future-ready)</Label>
                      <Input
                        value={role.bluetooth_device || ""}
                        onChange={(e) => updateRoleField(roleMeta.key, "bluetooth_device", e.target.value)}
                        placeholder="BT-Printer"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-border/40 pt-4 space-y-4">
          <h3 className="text-sm font-semibold">Automatic Printing Options</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean((restaurantInfo.printer_auto_print || {}).manual_orders)}
                onCheckedChange={(checked) => updateAutoPrint("manual_orders", checked)}
              />
              <Label>Auto Print Manual Orders</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean((restaurantInfo.printer_auto_print || {}).whatsapp_orders)}
                onCheckedChange={(checked) => updateAutoPrint("whatsapp_orders", checked)}
              />
              <Label>Auto Print WhatsApp Orders</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean((restaurantInfo.printer_auto_print || {}).online_orders)}
                onCheckedChange={(checked) => updateAutoPrint("online_orders", checked)}
              />
              <Label>Auto Print Online Orders</Label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Kitchen Copies</Label>
              <Select
                value={String((restaurantInfo.printer_copies_by_role || {}).kitchen || restaurantInfo.copies || 1)}
                onValueChange={(value) => updateCopiesByRole("kitchen", parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Copy</SelectItem>
                  <SelectItem value="2">2 Copies</SelectItem>
                  <SelectItem value="3">3 Copies</SelectItem>
                  <SelectItem value="4">4 Copies</SelectItem>
                  <SelectItem value="5">5 Copies</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cashier Copies</Label>
              <Select
                value={String((restaurantInfo.printer_copies_by_role || {}).cashier || restaurantInfo.copies || 1)}
                onValueChange={(value) => updateCopiesByRole("cashier", parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Copy</SelectItem>
                  <SelectItem value="2">2 Copies</SelectItem>
                  <SelectItem value="3">3 Copies</SelectItem>
                  <SelectItem value="4">4 Copies</SelectItem>
                  <SelectItem value="5">5 Copies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="border-t border-border/40 pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Printer Diagnostics</h3>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={fetchDiagnostics} disabled={loadingDiagnostics}>
                {loadingDiagnostics ? "Checking..." : "Refresh Status"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled
                title="Physical test printing requires the UniGate Print Agent"
              >
                Print Agent Required
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-sm text-muted-foreground">
            Automatic thermal delivery is inactive until an authenticated UniGate Print Agent is connected.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-muted-foreground">Configuration</div>
              <div className="font-medium">{diagnostics?.configuration_status || "Not Configured"}</div>
            </div>
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-muted-foreground">Print Agent</div>
              <div className="font-medium">{diagnostics?.agent_status || "Never Connected"}</div>
            </div>
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-muted-foreground">Printer Hardware</div>
              <div className="font-medium">{diagnostics?.hardware_status || "Unknown"}</div>
            </div>
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-muted-foreground">Automatic Delivery</div>
              <div className="font-medium">{diagnostics?.automatic_delivery_status || "Inactive - Print Agent required"}</div>
            </div>
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-muted-foreground">Agent Last Seen</div>
              <div className="font-medium">{diagnostics?.agent_last_seen_at || "Never"}</div>
            </div>
          </div>
        </div>

        <div className="border-t border-border/40 pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Operational Print Queue</h3>
            <Button type="button" variant="outline" size="sm" onClick={fetchPrintJobs} disabled={loadingJobs}>
              {loadingJobs ? "Loading..." : "Refresh Queue"}
            </Button>
          </div>
          {printJobs.length === 0 ? (
            <div className="rounded-md border border-border/50 p-3 text-sm text-muted-foreground">
              No queued, failed, or uncertain print jobs.
            </div>
          ) : (
            <div className="max-h-72 divide-y divide-border/50 overflow-y-auto rounded-md border border-border/50">
              {printJobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium">{job.document_type === "receipt" ? "Receipt" : "Kitchen Ticket"} · {job.printer_role}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      Order {job.order_id} · Attempt {job.attempt_count}
                    </div>
                    {job.failure_message && <div className="mt-1 text-xs text-red-700">{job.failure_message}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="outline">{job.status}</Badge>
                    {canRetryPrintJobs && ["failed", "unknown"].includes(job.status) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => retryPrintJob(job.id)}
                        disabled={retryingJobId === job.id}
                      >
                        {retryingJobId === job.id ? "Retrying..." : "Retry"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border/40 pt-4 space-y-3">
          <h3 className="text-sm font-semibold">Print Agent</h3>
          <div className="rounded-md border border-dashed border-border p-3">
            <div className="font-medium">Machine registration is not available yet</div>
            <div className="text-xs text-muted-foreground">Secure restaurant-bound agent enrollment will be delivered in P0-5B.</div>
          </div>
        </div>

        <Button
          onClick={() => handleSave("restaurant")}
          disabled={!canAdministerPrinters}
          style={{ backgroundColor: "#E57200" }}
        >
          Save
        </Button>

      </CardContent>
    </Card>
  );
};

export default PrinterSettings;
